import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, ChevronDown } from "lucide-react";
import { ENQUIRY_TYPES, PROPERTY_TYPES, BEDROOMS, RENTAL_SITUATIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-forest/15 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/35 transition-colors duration-300 focus:border-forest-700 focus:ring-2 focus:ring-forest-700/15";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-charcoal/60";

function Field({ label, id, children }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Select({ id, testid, value, onChange, children }) {
  return (
    <div className="relative">
      <select
        id={id}
        data-testid={testid}
        value={value}
        onChange={onChange}
        className={`${inputCls} appearance-none pr-10 ${value ? "" : "text-charcoal/35"}`}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal/40" />
    </div>
  );
}

export default function LeadForm({ source = "website", defaultEnquiry = "short_term", submitLabel = "Get Your Property Assessment", note = "No obligation. We'll review your property and respond within one working day." }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    property_type: "",
    bedrooms: "",
    rental_situation: "",
    enquiry_type: defaultEnquiry,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source });
      setDone(true);
      toast.success("Thank you — your property details are with us. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email stay@fieldgreenproperty.com.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-forest/15 bg-white p-10 text-center" data-testid="lead-form-success">
        <p className="font-serif text-3xl text-forest">Thank you, {form.name.split(" ")[0]}.</p>
        <p className="mt-4 text-sm leading-relaxed text-charcoal/70">
          We've received your property details and will review them against the current Dublin market.
          Expect to hear from us within one working day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-forest/15 bg-white p-7 sm:p-9 shadow-[0_30px_80px_rgba(10,27,20,0.18)]" data-testid="lead-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" id="lf-name">
          <input id="lf-name" data-testid="lead-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
        </Field>
        <Field label="Email" id="lf-email">
          <input id="lf-email" data-testid="lead-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
        </Field>
        <Field label="Phone" id="lf-phone">
          <input id="lf-phone" data-testid="lead-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+353 ..." className={inputCls} />
        </Field>
        <Field label="Property location" id="lf-location">
          <input id="lf-location" data-testid="lead-form-location-input" required value={form.location} onChange={set("location")} placeholder="e.g. Grand Canal Dock, Dublin 2" className={inputCls} />
        </Field>
        <Field label="Property type" id="lf-type">
          <Select id="lf-type" testid="lead-form-property-type-select" value={form.property_type} onChange={set("property_type")}>
            <option value="" disabled>Select…</option>
            {PROPERTY_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </Field>
        <Field label="Bedrooms" id="lf-bedrooms">
          <Select id="lf-bedrooms" testid="lead-form-bedrooms-select" value={form.bedrooms} onChange={set("bedrooms")}>
            <option value="" disabled>Select…</option>
            {BEDROOMS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </Select>
        </Field>
        <Field label="Current rental situation" id="lf-situation">
          <Select id="lf-situation" testid="lead-form-rental-situation-select" value={form.rental_situation} onChange={set("rental_situation")}>
            <option value="" disabled>Select…</option>
            {RENTAL_SITUATIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </Field>
        <Field label="Enquiry type" id="lf-enquiry">
          <Select id="lf-enquiry" testid="lead-form-enquiry-type-select" value={form.enquiry_type} onChange={set("enquiry_type")}>
            {ENQUIRY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Anything else? (optional)" id="lf-message">
          <textarea id="lf-message" data-testid="lead-form-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="Timeline, questions, corporate stay requirements…" className={`${inputCls} resize-none`} />
        </Field>
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="lead-form-submit-button"
        className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-7 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-forest-700 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {submitLabel}
        {!loading && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
      <p className="mt-4 text-center text-xs text-charcoal/45">
        {note}
      </p>
    </form>
  );
}
